import { formatUnitForDisplay } from "../../utils/Utils";

const getAcidMass = (mass) => (mass === "kg" ? 4032000 : 8870400);
const getAlkaliMass = (mass) => (mass === "kg" ? 7200000 : 15840000);
const getMaxMass = (mass) => (mass === "kg" ? 100 : 220);
const getMinTemp = (temperature) => (temperature === "C" ? 20 : 68);
const getMaxTemp = (temperature) => (temperature === "C" ? 100 : 212);
const DEFAULT_BASE_PRICE_RANGE = { min: 0.01, max: 1000 };
const KG_TO_LB_FACTOR = 2.20462;

export const DEFAULT_CIP_INFORMATION = (temperature, mass) => {
  return {
    acid: {
      acid_used_per_cip: {
        key: "acid_used_per_cip",
        labelKey: "amountPerCip",
        ranges: {
          hard: {
            min: 0.01,
            max: getAcidMass(mass),
          },
        },
        unit: mass,
      },
      acid_cip_ph: {
        key: "acid_cip_ph",
        labelKey: "foulingStrings.cip",
        ranges: { hard: { min: 1, max: 7 } },
        unit: "pH",
      },
      acid_cip_temp: {
        key: "acid_cip_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: formatUnitForDisplay(temperature),
      },
      acid_cip_duration: {
        key: "acid_cip_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
    alkali: {
      alkali_used_per_cip: {
        key: "alkali_used_per_cip",
        labelKey: "amountPerCip",
        ranges: {
          hard: {
            min: 0.01,
            max: getAlkaliMass(mass),
          },
        },
        unit: mass,
      },
      alkali_cip_ph: {
        key: "alkali_cip_ph",
        labelKey: "foulingStrings.cip",
        ranges: { hard: { min: 7, max: 14 } },
        unit: "pH",
      },
      alkali_cip_temp: {
        key: "alkali_cip_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: formatUnitForDisplay(temperature),
      },
      alkali_cip_duration: {
        key: "alkali_cip_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
    additive_alkali: {
      additive_alkali_used_per_cip: {
        key: "additive_alkali_used_per_cip",
        labelKey: "amountPerCip",
        ranges: {
          hard: {
            min: 0.01,
            max: getMaxMass(mass),
          },
        },
        unit: mass,
      },
      additive_alkali_ph: {
        key: "additive_alkali_ph",
        labelKey: "foulingStrings.cip",
        ranges: { hard: { min: 1, max: 14 } },
        unit: "pH",
      },
      additive_alkali_temp: {
        key: "additive_alkali_temp",
        labelKey: "cip_additive_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: formatUnitForDisplay(temperature),
      },
      additive_alkali_duration: {
        key: "additive_alkali_duration",
        labelKey: "cip_additive_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
    additive_acid: {
      additive_acid_used_per_cip: {
        key: "additive_acid_used_per_cip",
        labelKey: "amountPerCip",
        ranges: {
          hard: {
            min: 0.01,
            max: getMaxMass(mass),
          },
        },
        unit: mass,
      },
      additive_acid_ph: {
        key: "additive_acid_ph",
        labelKey: "foulingStrings.cip",
        ranges: { hard: { min: 1, max: 14 } },
        unit: "pH",
      },
      additive_acid_temp: {
        key: "additive_acid_temp",
        labelKey: "cip_additive_temp",
        ranges: {
          hard: {
            min: getMinTemp(temperature),
            max: getMaxTemp(temperature),
          },
        },
        unit: formatUnitForDisplay(temperature),
      },
      additive_acid_duration: {
        key: "additive_acid_duration",
        labelKey: "cip_additive_duration",
        ranges: { hard: { min: 1, max: 48 } },
        unit: "hrs",
      },
    },
  };
};

export const CIP_SECTION_META = {
  acid: {
    chemicalLabelKey: "defaultInformationStrings.acid",
    priceKey: "acid_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "acid_chem_id",
  },
  alkali: {
    chemicalLabelKey: "defaultInformationStrings.alkali",
    priceKey: "alkali_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "alkali_chem_id",
  },
  additive_alkali: {
    chemicalLabelKey: "defaultInformationStrings.additive",
    priceKey: "additive_alkali_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "additive_alkali_chem_id",
  },
  additive_acid: {
    chemicalLabelKey: "defaultInformationStrings.additive",
    priceKey: "additive_acid_price",
    priceRange: { min: 0.01, max: 1000 },
    chemicalKey: "additive_acid_chem_id",
  },
};

export const DEFAULT_CIP_SECTION_KEYS = Object.keys(CIP_SECTION_META);

export const getCIPPriceRangeByCategory = (
  category,
  currency,
  mass,
  conversionRate,
) => {
  const {
    min = DEFAULT_BASE_PRICE_RANGE.min,
    max = DEFAULT_BASE_PRICE_RANGE.max,
  } = CIP_SECTION_META?.[category]?.priceRange || {};

  let rangeMin = Number(min);
  let rangeMax = Number(max);

  if (Number.isNaN(rangeMin) || Number.isNaN(rangeMax)) {
    rangeMin = DEFAULT_BASE_PRICE_RANGE.min;
    rangeMax = DEFAULT_BASE_PRICE_RANGE.max;
  }

  if (mass === "lb") {
    rangeMin *= KG_TO_LB_FACTOR;
    rangeMax *= KG_TO_LB_FACTOR;
  }

  const parsedRate = Number(conversionRate);
  if (currency !== "USD" && !Number.isNaN(parsedRate) && parsedRate > 0) {
    rangeMin *= parsedRate;
    rangeMax *= parsedRate;
  }

  return {
    min: Number(rangeMin.toFixed(2)),
    max: Number(rangeMax.toFixed(2)),
  };
};

export const getCIPPriceRanges = (
  currency,
  mass,
  conversionRate,
  categories = DEFAULT_CIP_SECTION_KEYS,
) =>
  categories.reduce((acc, category) => {
    acc[category] = getCIPPriceRangeByCategory(
      category,
      currency,
      mass,
      conversionRate,
    );
    return acc;
  }, {});

export const PLANT_DETAILS = () => {
  return {
    add: {
      add_info: {
        key: "add_info",
        multiline: "2",
        maxChar: 500,
      },
    },
  };
};

export const INITIAL_DEFAULT_CIP_ERROR_STATE = {
  acid_price: null,
  alkali_price: null,
  additive_alkali_price: null,
  additive_acid_price: null,
  acid_used_per_cip: null,
  alkali_used_per_cip: null,
  additive_alkali_used_per_cip: null,
  additive_acid_used_per_cip: null,
  acid_cip_ph: null,
  alkali_cip_ph: null,
  additive_alkali_ph: null,
  additive_acid_ph: null,
  acid_cip_temp: null,
  alkali_cip_temp: null,
  additive_alkali_temp: null,
  additive_acid_temp: null,
  acid_cip_duration: null,
  alkali_cip_duration: null,
  additive_alkali_duration: null,
  additive_acid_duration: null,
};

export const CIP_SEQUENCE_OPTIONS = [
  { label: "Acid", value: "acid" },
  { label: "Alkali", value: "alkali" },
  { label: "Acid Solution", value: "acid solution" },
  { label: "Alkali Solution", value: "alkali solution" },
];

export const DEFAULT_CIP_SEQUENCE = { text: "alkali->acid", id: "CS003" };

export const CIP_SEQUENCE_TEXT_TO_ID_MAP = {
  acid: "CS001",
  alkali: "CS002",
  "alkali→acid": "CS003",
  "acid→alkali": "CS004",
  "acid solution": "CS005",
  "alkali solution": "CS006",
  "acid→alkali solution": "CS007",
  "alkali→acid solution": "CS008",
  "acid solution→alkali": "CS009",
  "alkali solution→acid": "CS010",
  "acid solution→alkali solution": "CS011",
  "alkali solution→acid solution": "CS012",
};
